/**
 * Vue 3 custom renderer over the UINode tree (@vue/runtime-core only — no runtime-dom).
 * One renderer per document (host ops close over it); one Vue app per UICanvas.
 */
import { createRenderer, h, type App, type Component } from '@vue/runtime-core';
import type { UIDocument } from '../tree/UIDocument';
import type { UINode } from '../tree/UINode';

export function createUIRenderer(doc: UIDocument) {
  return createRenderer<UINode, UINode>({
    createElement: (type) => doc.createElement(type),
    createText: (text) => doc.createRawText(text),
    createComment: () => doc.createComment(),
    setText: (node, text) => doc.setRawText(node, text),
    setElementText: (el, text) => doc.setElementText(el, text),
    insert: (child, parent, anchor) => doc.insert(child, parent, anchor ?? null),
    remove: (child) => doc.remove(child),
    parentNode: (node) => node.parent,
    nextSibling: (node) => node.nextSibling(),
    patchProp: (el, key, _prev, next) => doc.patchProp(el, key, next),
  });
}

/**
 * Mount a (compiled .uix) component into a document. Caller owns `app.unmount()`.
 *
 * When `props` is provided the root renders THROUGH it (a thin wrapper component), so a
 * `shallowReactive` object stays LIVE: external mutation (UIScreenBridge.setProps) re-renders
 * with the new values, and `on<Event>` keys receive the root's `emit(...)`s — plain
 * `createApp(component, props)` rootProps are a one-shot snapshot instead.
 */
export function createUIApp(
  doc: UIDocument,
  component: Component,
  props?: Record<string, unknown> | null,
): App<UINode> {
  const root: Component = props ? { setup: () => () => h(component, props) } : component;
  const app = createUIRenderer(doc).createApp(root);
  app.mount(doc.root);
  return app;
}
